import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X, CaretDown } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

const SUB_MENUS = {
  "/services": [
    { to: "/services/deplacements", label: "Déplacements sur mesure" },
    { to: "/services/greet-meet", label: "Meet & Greet" },
    { to: "/services/securite", label: "Sécurité rapprochée" },
    { to: "/services/transport-luxe", label: "Transport de luxe" },
  ],
  "/property-services": [
    { to: "/property-services/automobile", label: "Nettoyage Automobile" },
    { to: "/property-services/mobilier", label: "Nettoyage Mobilier" },
    { to: "/property-services/appartement", label: "Nettoyage Appartement" },
    { to: "/property-services/commercial", label: "Locaux Commerciaux" },
  ],
  "/destinations": [
    { to: "/destinations/chateau-versailles", label: "Château de Versailles" },
    { to: "/destinations/fontainebleau", label: "Château de Fontainebleau" },
    { to: "/destinations/giverny-monet", label: "Giverny — Jardins de Monet" },
    { to: "/destinations/reims-champagne", label: "Reims & Champagne" },
    { to: "/destinations/chateaux-loire", label: "Châteaux de la Loire" },
    { to: "/destinations/plages-debarquement-normandie", label: "Plages du Débarquement" },
    { to: "/destinations/bruges-belgique", label: "Bruges — Belgique" },
  ],
  "/experiences": [
    { to: "/experiences/mariage", label: "Mariage" },
    { to: "/experiences/paris-sur-mesure", label: "Paris sur mesure" },
    { to: "/experiences/fashion-week", label: "Fashion Week" },
    { to: "/experiences/shopping-luxe", label: "Shopping luxe" },
  ],
};

const Navigation = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setOpenSub(null);
  }, [location]);

  const navLinks = [
    { to: "/services", label: t("nav.services") },
    { to: "/property-services", label: "Property Services" },
    { to: "/destinations", label: t("nav.destinations") },
    { to: "/experiences", label: t("nav.experiences") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const toggleSub = (path) => {
    setOpenSub(openSub === path ? null : path);
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");
  const solidNav = scrolled || location.pathname.startsWith("/property-services") || location.pathname.startsWith("/services/chauffeur-prive-");

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solidNav ? "bg-[#EDEBE4]/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-20">
          <Link 
            to="/" 
            className="flex items-center gap-4"
            data-testid="nav-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_parisgo/artifacts/m1auxpeg_Copie%20de%20White%20and%20Red%20Elegant%20Luxury%20Adelire%20Jewelry%20Logo.png" 
              alt="ALTURA" 
              className="h-14 w-auto"
            />
            <span className="font-heading text-xl tracking-[0.35em] text-[#1A1A1A] uppercase hidden sm:inline">
              Altura
            </span>
          </Link>

          {/* Desktop */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm tracking-wide transition-colors ${
                  isActive(link.to)
                    ? "text-[#1A1A1A]"
                    : "text-[#1A1A1A]/60 hover:text-[#1A1A1A]"
                }`}
                data-testid={`nav-link-${link.to.replace("/", "")}`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/services/deplacements"
              className="btn-dark text-xs px-6 py-3"
              data-testid="nav-book-btn"
            >
              {t("nav.book")}
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden text-[#1A1A1A] p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="nav-mobile-toggle"
          >
            {isOpen ? <X size={24} weight="light" /> : <List size={24} weight="light" />}
          </button>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div className="md:hidden absolute left-0 right-0 top-20 bg-[#EDEBE4] border-t border-[#1A1A1A]/10 shadow-lg max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="nav-mobile-menu">
            <div className="flex flex-col px-6 sm:px-12 py-4 gap-0">
              {navLinks.map((link) => {
                const subs = SUB_MENUS[link.to];
                const hasSubs = !!subs;
                const isSubOpen = openSub === link.to;

                return (
                  <div key={link.to}>
                    {hasSubs ? (
                      <button
                        type="button"
                        onClick={() => toggleSub(link.to)}
                        className={`w-full flex items-center justify-between py-3.5 px-4 text-sm tracking-wide transition-colors rounded-sm ${
                          isActive(link.to)
                            ? "text-[#1A1A1A] font-medium"
                            : "text-[#1A1A1A]/70"
                        }`}
                        data-testid={`nav-mobile-${link.to.replace("/", "")}`}
                      >
                        <span>{link.label}</span>
                        <CaretDown
                          size={14}
                          weight="bold"
                          className={`text-[#1A1A1A]/40 transition-transform duration-200 ${isSubOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                    ) : (
                      <Link
                        to={link.to}
                        className={`block py-3.5 px-4 text-sm tracking-wide transition-colors rounded-sm ${
                          isActive(link.to)
                            ? "text-[#1A1A1A] font-medium"
                            : "text-[#1A1A1A]/70 hover:text-[#1A1A1A]"
                        }`}
                        data-testid={`nav-mobile-${link.to.replace("/", "")}`}
                      >
                        {link.label}
                      </Link>
                    )}

                    {/* Sub-menu */}
                    {hasSubs && isSubOpen && (
                      <div className="ml-4 border-l-2 border-[#1A1A1A]/10 mb-2">
                        <Link
                          to={link.to}
                          className="block py-2.5 pl-4 pr-4 text-xs tracking-wider uppercase text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors"
                          data-testid={`nav-mobile-sub-all-${link.to.replace("/", "")}`}
                        >
                          Tout voir
                        </Link>
                        {subs.map((sub) => (
                          <Link
                            key={sub.to}
                            to={sub.to}
                            className={`block py-2.5 pl-4 pr-4 text-sm transition-colors ${
                              location.pathname === sub.to
                                ? "text-[#1A1A1A] font-medium bg-[#1A1A1A]/5"
                                : "text-[#1A1A1A]/60 hover:text-[#1A1A1A] hover:bg-[#1A1A1A]/5"
                            }`}
                            data-testid={`nav-mobile-sub-${sub.to.split("/").pop()}`}
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="pt-3 mt-2 border-t border-[#1A1A1A]/10">
                <Link
                  to="/services/deplacements"
                  className="btn-dark text-xs px-6 py-3 text-center block"
                  data-testid="nav-mobile-book-btn"
                >
                  {t("nav.book")}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
